import { rowExtras, setRowExtras } from "../row-extra.ts";
import { createSettingControl, readSettingControl } from "./setting-fields.ts";
import type { ExtraField } from "./setting-fields.ts";
import { radioEventParams, trackEvent } from "./analytics.ts";
import { requireRuntimeApi } from "./state.ts";
import type { RuntimeApi } from "../runtime-rpc.ts";
import type { UiContext } from "../types/ui-context.js";
import type { ChannelRow, ColumnMeta } from "./channel-values.ts";

// The bulk channel editor: one modal that writes the same value to every
// selected channel (issue #146).
//
// Editing twenty channels one cell at a time is the grid's only answer to
// "give this whole block the repeater's tone", and it is the wrong one: the
// same value has to be retyped per row, and a driver's per-channel extras
// (Busy Channel Lockout, PTT-ID, scramble) are a modal apiece on top of that.
// This is the one place that does it once.
//
// Two things decide what the modal offers:
//   * the grid's own columns, minus Location (the memory slot is what tells
//     channels apart, so one value across a selection is never right) and
//     minus anything the selected driver marks read-only;
//   * the driver's per-channel extras, read through the same
//     get_channel_extra RPC the single-channel editor uses
//     (web/python/webchirp_bridge/channel_extra.py).
//
// Every field carries its own "apply this" checkbox and nothing is written
// without one ticked. That is what makes "leave the rest alone" the default
// rather than a thing the user has to arrange: a bulk edit opened on twenty
// channels that disagree about Mode must not quietly give all twenty the first
// one's Mode just because the control had to show something.
export function createChannelBulkEdit(ctx: UiContext) {
  const { dom, state, log } = ctx;

  // The rows the modal opened on, and one entry per rendered field. All three
  // are dropped on close, which is also what makes a late extras response
  // harmless: it finds a closed modal and returns.
  let editedRows: ChannelRow[] = [];
  let columnFields: Array<ReturnType<typeof appendField> & { column: string }> = [];
  let extraFields: Array<ReturnType<typeof appendField> & { field: ExtraField }> = [];
  // What the fields were built from, checked again on apply. See schemaSnapshot.
  let editedSchema: ReturnType<typeof schemaSnapshot> | null = null;
  // The toolbar button the open came from, refocused when the modal closes.
  let triggerElement: HTMLElement | null = null;
  // Bumped on every open so the response to a superseded open cannot render
  // over the one the user is looking at.
  let openToken = 0;

  const COLUMN_ID_PREFIX = "channel-bulk-column-";
  const EXTRA_ID_PREFIX = "channel-bulk-extra-";

  function isModalOpen() {
    return !dom.channelBulkEditModalEl.classList.contains("hidden");
  }

  function setModalOpen(open: boolean): void {
    dom.channelBulkEditModalEl.classList.toggle("hidden", !open);
    if (!open) {
      editedRows = [];
      columnFields = [];
      extraFields = [];
      editedSchema = null;
      // Hand the keyboard back to where it came from; without this it is left
      // inside a hidden dialog and the next Tab starts from the top of the page.
      triggerElement?.focus?.();
      triggerElement = null;
    }
  }

  function closeModal() {
    setModalOpen(false);
  }

  function setMessage(text: string): void {
    const message = String(text || "");
    dom.channelBulkEditMessageEl.textContent = message;
    dom.channelBulkEditMessageEl.hidden = message === "";
  }

  function setExtraMessage(text: string): void {
    const message = String(text || "");
    dom.channelBulkEditExtraMessageEl.textContent = message;
    dom.channelBulkEditExtraMessageEl.hidden = message === "";
  }

  // The toolbar control only means anything with a selection behind it, so it
  // follows one. The grid owns the selection and calls this on every change to
  // it, which is the only thing that can move this button.
  function refreshAvailability() {
    const count = ctx.table.selectedChannelRows().length;
    dom.channelBulkEditEl.disabled = count === 0;
    dom.channelBulkEditEl.title = count === 0
      ? "Select one or more channels to edit them together"
      : `Edit the ${count} selected channel(s) together`;
  }

  // The driver and schema the fields were built from, as two things that can be
  // compared later: the radio the user had selected, and the identity of the
  // metadata object itself.
  //
  // Picking another radio while this modal is open does not disturb the rows --
  // reloadForSelectedRadio in web/js/ui/radio-catalog.ts keeps them and swaps
  // the schema underneath, asynchronously -- so the rows-still-present check in
  // apply() cannot see it. What it changes is exactly what the fields were
  // derived from: a column the new driver does not publish would be counted as
  // applied without landing anywhere, and a column it marks read-only would be
  // written anyway, because the check here passes allowReadOnly.
  function schemaSnapshot() {
    return {
      radioKey: state.selectedRadio?.key || "",
      metadata: state.radioMetadata,
    };
  }

  // The columns a bulk edit may write. Location is excluded because a memory
  // slot is a channel's identity rather than an attribute, and a column the
  // driver reports as read-only is excluded because the radio would not take
  // it -- the grid greys those cells out for the same reason.
  function editableColumns() {
    return state.currentHeaders.filter((column) => {
      if (column === "Location") {
        return false;
      }
      const meta = state.radioMetadata.columns?.[column];
      // No metadata at all is an unconstrained column, not an unsupported one:
      // until a radio is selected the grid runs on the generic-CSV schema and
      // writes anything through. See findEnumOption in
      // web/js/ui/channel-table.ts, which reads an absent option list the same
      // way.
      return !meta || meta.editable !== false;
    });
  }

  // What the selection already holds for a column: the first row's value, and
  // whether the rows disagree. A disagreement is shown rather than resolved --
  // the control has to display something, and saying "multiple values" beside
  // it is what stops that something from reading as the selection's value.
  function commonValue<T>(values: readonly T[]): { value: T | undefined; mixed: boolean } {
    const first = values[0];
    return {
      value: first,
      mixed: values.some((each) => String(each ?? "") !== String(first ?? "")),
    };
  }

  // The control for one grid column, built from the same CHIRP column metadata
  // the grid's own cell editors are built from (createCellEditor in
  // web/js/ui/channel-table.ts).
  function createColumnControl(column: string, initial: string | undefined): HTMLInputElement | HTMLSelectElement {
    const meta: Partial<ColumnMeta> = state.radioMetadata.columns?.[column] || {};
    if (meta.kind === "enum" && Array.isArray(meta.options) && meta.options.length > 0) {
      const select = document.createElement("select");
      const options = meta.options.map(String);
      for (const option of options) {
        const optionEl = document.createElement("option");
        optionEl.value = option;
        optionEl.textContent = option;
        select.appendChild(optionEl);
      }
      let wanted = String(initial ?? "");
      if (wanted !== "" && !options.includes(wanted)) {
        // A value the driver's list does not carry (a codeplug edited
        // elsewhere, rows carried over from another radio) is offered rather
        // than silently replaced, exactly as the grid's enum cells do.
        const optionEl = document.createElement("option");
        optionEl.value = wanted;
        optionEl.textContent = wanted;
        select.appendChild(optionEl);
      }
      if (wanted === "") {
        // A blank enum is what an unset Power looks like, and a <select> cannot
        // be cleared back to it -- so a blank start would leave the one value
        // the column cannot accept sitting in a control the user may tick.
        // Falling back to the driver's first option keeps every applicable
        // value applicable; the checkbox is what decides whether it is written.
        wanted = options[0];
      }
      select.value = wanted;
      return select;
    }
    const input = document.createElement("input");
    const numeric = meta.kind === "int";
    input.type = numeric ? "number" : "text";
    if (numeric) {
      if (Number.isFinite(meta.min)) {
        input.min = String(meta.min);
      }
      if (Number.isFinite(meta.max)) {
        input.max = String(meta.max);
      }
    }
    if (Number.isFinite(meta.maxLength)) {
      input.maxLength = Number(meta.maxLength);
    }
    input.value = String(initial ?? "");
    return input;
  }

  // One field row: the "apply this" checkbox with its label, then the control
  // and its error slot. Returns the entry the apply path reads the field back
  // through.
  /**
   * @param field doc: the driver's help text.
   */
  function appendField(
    grid: HTMLElement,
    { id, name, label, doc, mixed, control }: { id: string; name: string; label: string; doc?: string; mixed: boolean; control: HTMLInputElement | HTMLSelectElement },
  ) {
    const labelCell = document.createElement("div");
    labelCell.className = "bulk-edit-label";
    // The checkbox sits inside the label, so the field's name is its own click
    // target and needs no htmlFor pointing at a second id.
    const toggle = document.createElement("label");
    toggle.className = "bulk-edit-toggle";
    const apply = document.createElement("input");
    apply.type = "checkbox";
    apply.className = "bulk-edit-apply";
    apply.name = `${name}__apply`;
    // The wrapping label names the field, which is the right visible text and
    // the wrong accessible name for this box: a boolean extra puts a second
    // checkbox beside it -- its value -- and a screen reader would announce
    // both as "Busy Channel Lockout, checkbox" with nothing to say which one
    // decides that the field is written and which one is what gets written.
    apply.setAttribute("aria-label", `Apply ${label}`);
    toggle.appendChild(apply);
    const text = document.createElement("span");
    text.textContent = label;
    toggle.appendChild(text);
    labelCell.appendChild(toggle);
    if (mixed) {
      const note = document.createElement("div");
      note.className = "bulk-edit-mixed";
      note.textContent = "multiple values";
      labelCell.appendChild(note);
    }
    if (doc) {
      const docEl = document.createElement("div");
      docEl.className = "bulk-edit-doc";
      docEl.textContent = doc;
      labelCell.appendChild(docEl);
    }

    const controlCell = document.createElement("div");
    controlCell.className = "bulk-edit-control";
    control.id = id;
    control.name = name;
    // The visible label belongs to the checkbox, so the control needs its own
    // accessible name or a screen reader announces it as unlabelled.
    control.setAttribute("aria-label", label);
    // Touching a control is as good as ticking its box: having to arm a field
    // before it accepts a value is the kind of two-step nobody expects, and the
    // box still has to be reachable on its own for a checkbox field, where the
    // control is a checkbox too.
    const arm = () => {
      apply.checked = true;
    };
    control.addEventListener("input", arm);
    control.addEventListener("change", arm);
    controlCell.appendChild(control);
    const errorEl = document.createElement("div");
    errorEl.className = "bulk-edit-error";
    errorEl.hidden = true;
    controlCell.appendChild(errorEl);

    grid.appendChild(labelCell);
    grid.appendChild(controlCell);
    return {
      control,
      apply,
      setError(message: string) {
        errorEl.textContent = String(message || "");
        errorEl.hidden = !message;
        controlCell.classList.toggle("is-invalid", Boolean(message));
      },
    };
  }

  function renderColumnFields(rows: readonly ChannelRow[]): void {
    dom.channelBulkEditGridEl.innerHTML = "";
    columnFields = editableColumns().map((column) => {
      const { value, mixed } = commonValue(rows.map((row) => String(row?.[column] ?? "")));
      const entry = appendField(dom.channelBulkEditGridEl, {
        id: `${COLUMN_ID_PREFIX}${column}`,
        name: column,
        label: column,
        mixed,
        control: createColumnControl(column, value),
      });
      return { ...entry, column };
    });
    dom.channelBulkEditApplyEl.disabled = columnFields.length === 0;
  }

  function renderExtraFields(fields: readonly ExtraField[], rows: readonly ChannelRow[]): void {
    dom.channelBulkEditExtraGridEl.innerHTML = "";
    extraFields = fields.map((field) => {
      // A row's own value wins over the one the driver reported for the slot,
      // exactly as the single-channel editor resolves it: the sidecar is what
      // the channel carries, wherever it has been moved to since.
      const { value, mixed } = commonValue(rows.map((row) => {
        const stored = rowExtras(row) || {};
        return Object.hasOwn(stored, field.name) ? stored[field.name] : field.current;
      }));
      const entry = appendField(dom.channelBulkEditExtraGridEl, {
        id: `${EXTRA_ID_PREFIX}${field.name}`,
        name: field.name,
        label: field.label || field.name,
        doc: field.doc,
        mixed,
        control: createSettingControl(field, value),
      });
      return { ...entry, field };
    });
    dom.channelBulkEditApplyEl.disabled = false;
  }

  // Whether a response has been overtaken: the modal has been closed (which
  // drops the rows it was opened on) or re-opened on another selection. Both
  // have to be checked -- a token alone misses a close, and rendering into a
  // hidden dialog leaves fields there for whatever opens it next.
  function superseded(rows: readonly ChannelRow[], token: number): boolean {
    return token !== openToken || editedRows !== rows;
  }

  // Fetch the driver's per-channel settings schema for the selection.
  //
  // Extras are per-memory in principle, so there is no schema for a set of
  // channels -- only for one. The first selected channel's slot is what is
  // read, and the message above the section says so, because a driver that did
  // vary its extras by slot would otherwise offer fields the rest of the
  // selection has no equivalent for. The upload path ignores names a
  // destination memory does not have (_apply_row_extras_to_memory in
  // web/python/webchirp_bridge/channel_extra.py), so the failure mode is a
  // setting that does not land, not a corrupted channel.
  async function loadExtraFields(rows: ChannelRow[], token: number): Promise<void> {
    const radio = state.selectedRadio;
    if (!radio) {
      setExtraMessage("Select a radio to edit driver-specific channel settings.");
      return;
    }
    const location = String(rows[0]?.Location ?? "").trim();
    setExtraMessage("Reading the driver's per-channel settings...");
    let payload: Awaited<ReturnType<RuntimeApi["getChannelExtra"]>> | null = null;
    try {
      payload = await requireRuntimeApi(state).getChannelExtra({
        sessionId: await ctx.session.currentId(),
        location,
      });
    } catch (error) {
      if (superseded(rows, token)) {
        return;
      }
      setExtraMessage("Extra settings could not be read for these channels.");
      log.reportActionError("Bulk channel edit", error);
      return;
    }
    if (superseded(rows, token)) {
      return;
    }
    if (!payload?.available) {
      setExtraMessage(payload?.message || "This radio has no extra settings for its channels.");
      return;
    }
    // Immutable settings are dropped rather than shown disabled the way the
    // single-channel editor shows them: there they are context for the channel
    // being edited, here they are a field that could be ticked and could never
    // be applied.
    const fields = (payload.fields || []).filter((field) => field.mutable !== false);
    if (fields.length === 0) {
      setExtraMessage("This radio has no editable extra settings for its channels.");
      return;
    }
    setExtraMessage(
      `Read from channel ${location || "?"}; whatever you tick here is written to `
      + `all ${rows.length} selected channel(s).`,
    );
    renderExtraFields(fields, rows);
  }

  // Open the editor on the current selection. The columns render immediately —
  // they need nothing but the schema the grid already has — while the extras
  // are fetched, so a slow first call (this can be the one that boots Pyodide)
  // leaves a usable dialog rather than an empty one.
  function open(trigger: HTMLElement | null = null) {
    const rows = ctx.table.selectedChannelRows();
    if (rows.length === 0) {
      log.setStatus("Select one or more channels to edit them in bulk.");
      return Promise.resolve();
    }
    if (!state.currentHeaders.length) {
      log.setStatus("No channel schema loaded yet.");
      return Promise.resolve();
    }
    // Where focus goes back to on every close path. The caller hands the button
    // over rather than this reading document.activeElement, because a click does
    // not focus a button on every platform (Safari does not).
    triggerElement = trigger;
    const token = openToken + 1;
    openToken = token;
    editedRows = rows;
    editedSchema = schemaSnapshot();
    dom.channelBulkEditTitleEl.textContent =
      `Edit ${rows.length} selected channel${rows.length === 1 ? "" : "s"}`;
    setMessage("Tick an attribute to give every selected channel the same value. "
      + "Anything left unticked is left as it is.");
    renderColumnFields(rows);
    dom.channelBulkEditExtraGridEl.innerHTML = "";
    extraFields = [];
    setExtraMessage("");
    setModalOpen(true);
    // Immediately, not when the extras arrive: until focus is inside the dialog
    // the keyboard is still on the toolbar button behind the overlay.
    (columnFields[0]?.apply ?? dom.channelBulkEditCancelEl).focus?.();
    trackEvent("channels_bulk_edit_opened", {
      ...radioEventParams(state.selectedRadio),
      channel_count: rows.length,
    });
    return loadExtraFields(rows, token);
  }

  // Whether the editor's rows or the schema its fields were built from have
  // moved since it opened, closing it with a status line if so. Asked before
  // the values are checked and again once the runtime has answered, because
  // the check waits its turn in the runtime's queue.
  function abandonedBecauseStale(rows: readonly ChannelRow[]): boolean {
    // A download or an image load replaces state.currentRows wholesale without
    // closing this modal, which would leave the apply mutating rows that are no
    // longer in the editor -- reported as success, absent from what is later
    // uploaded.
    if (rows.some((row) => !state.currentRows.includes(row))) {
      setModalOpen(false);
      log.setStatus("The channel list changed while the bulk editor was open; nothing was changed.");
      return true;
    }
    // The same refusal for the other half of what these fields were built from:
    // the driver and its schema. See schemaSnapshot for what a late metadata
    // load would otherwise let through.
    const schema = schemaSnapshot();
    if (schema.radioKey !== editedSchema?.radioKey || schema.metadata !== editedSchema?.metadata) {
      setModalOpen(false);
      log.setStatus("The selected radio changed while the bulk editor was open; nothing was changed.");
      return true;
    }
    return false;
  }

  // Write the ticked values onto every selected channel.
  //
  // Everything is checked before anything is written. A bulk edit that gave
  // up half way would leave the selection in two states with nothing to say
  // which channels took the value, and a rejected value is invisible in a row
  // -- a tone the radio's table lacks keeps the cell's old tone rather than
  // raising an error. So the ticked values for every selected row go to the
  // runtime in one call (previewRowEdits in web/js/ui/channel-table.ts, which
  // applies the radio's own rules, web/python/webchirp_bridge/row_normalization.py),
  // and a value this radio will not take stops the whole apply and is reported
  // on its own field. What the driver says about the rows it does take is
  // highlighted in the grid, as for any committed cell.
  async function apply(): Promise<void> {
    const rows = editedRows;
    if (rows.length === 0) {
      setModalOpen(false);
      return;
    }
    if (abandonedBecauseStale(rows)) {
      return;
    }

    const columnWrites: Array<{ column: string; value: string }> = [];
    const extraWrites: Record<string, unknown> = {};
    let invalid = 0;
    for (const entry of columnFields) {
      entry.setError("");
      if (entry.apply.checked) {
        columnWrites.push({ column: entry.column, value: String(entry.control.value ?? "") });
      }
    }
    for (const entry of extraFields) {
      entry.setError("");
      if (!entry.apply.checked) {
        continue;
      }
      // rejectUnlistedValue: the control may be showing a value the first
      // selected channel carries and this driver no longer offers, which is
      // right for that one channel and wrong for every other channel in the
      // selection. Copying it is what this dialog does, so it is refused here.
      const { value, error } = readSettingControl(entry.field, entry.control, {
        rejectUnlistedValue: true,
      });
      if (error) {
        entry.setError(error);
        invalid += 1;
        continue;
      }
      extraWrites[entry.field.name] = value;
    }
    const extraCount = Object.keys(extraWrites).length;
    const fieldCount = columnWrites.length + extraCount;
    if (invalid === 0 && fieldCount === 0) {
      setMessage("Tick at least one attribute to change, or cancel.");
      return;
    }

    let preview: Awaited<ReturnType<UiContext["table"]["previewRowEdits"]>> = null;
    if (columnWrites.length > 0) {
      dom.channelBulkEditApplyEl.disabled = true;
      setMessage("Checking the values with the selected radio...");
      const edits = columnWrites.map(({ column, value }) => ({ column, value, allowReadOnly: true }));
      preview = await ctx.table.previewRowEdits(rows.map((row) => ({ row, edits })));
      if (!isModalOpen() || editedRows !== rows || abandonedBecauseStale(rows)) {
        return;
      }
      dom.channelBulkEditApplyEl.disabled = false;
      if (!preview) {
        setMessage("The values could not be checked; see Debug Output.");
        return;
      }
      // A value is refused when any selected row refuses it. The column rules
      // do not depend on the row, so in practice that is all of them.
      const refused = new Set<string>();
      for (const result of preview.results) {
        for (const cell of result.cells) {
          if (!cell.accepted) {
            refused.add(cell.column);
          }
        }
      }
      for (const entry of columnFields) {
        if (refused.has(entry.column)) {
          entry.setError(`The selected radio does not accept this ${entry.column}.`);
          invalid += 1;
        }
      }
    }
    if (invalid > 0) {
      setMessage(`Fix ${invalid} highlighted value${invalid === 1 ? "" : "s"} before applying.`);
      return;
    }

    // The values the preflight objected to in these cells are no longer the
    // values in them, so their highlights no longer describe anything. Every
    // other flagged cell is left marked: a failed upload highlights issues
    // across the whole grid, and one bulk edit is no reason to stop showing the
    // ones it did not touch. Cleared before the answers are applied, which put
    // back whatever the driver says about the rows as they now stand.
    ctx.table.clearInvalidHighlightsForCells(rows, columnWrites.map(({ column }) => column));
    if (preview && !preview.apply()) {
      setModalOpen(false);
      log.setStatus("The channels changed while their values were being checked; nothing was changed.");
      return;
    }
    if (extraCount > 0) {
      for (const row of rows) {
        // A fresh object per row: setRowExtras merges into whatever the row
        // already carries, and one shared object would leave every row holding
        // the same mapping.
        setRowExtras(row, { ...extraWrites });
      }
      // The driver judges a channel with its extras, so a check in flight for
      // one of these rows answers for a row that no longer exists.
      ctx.table.rowsRewritten(rows);
    }
    ctx.table.render();
    setModalOpen(false);
    trackEvent("channels_bulk_edited", {
      ...radioEventParams(state.selectedRadio),
      channel_count: rows.length,
      field_count: fieldCount,
    });
    log.setStatus(
      `Applied ${fieldCount} attribute(s) to ${rows.length} selected channel(s)`
      + `${extraCount > 0 ? "; extra settings are written on upload or image export." : "."}`,
    );
  }

  function bindEvents() {
    dom.channelBulkEditEl.addEventListener("click", () => {
      open(dom.channelBulkEditEl);
    });
    dom.channelBulkEditCancelEl.addEventListener("click", () => {
      setModalOpen(false);
    });
    dom.channelBulkEditModalEl.addEventListener("click", (event) => {
      if (event.target === dom.channelBulkEditModalEl) {
        setModalOpen(false);
      }
    });
    dom.channelBulkEditFormEl.addEventListener("submit", (event) => {
      event.preventDefault();
      void apply();
    });
  }

  return { bindEvents, isModalOpen, closeModal, open, refreshAvailability };
}
